import { ArrowUpRight, ImageOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { ContentLocale, PublicContentRecord } from '@/data/public-catalog'
import { sourceMedia } from '@/data/media-manifest'

function sourceProducer(product: PublicContentRecord): string | null {
  const title = product.languages.it?.title
  if (!title) return null
  const separator = title.lastIndexOf(' – ')
  if (separator >= 0) return title.slice(separator + 3)
  const hyphen = title.match(/ -\s*([\p{L}\d]+)$/u)
  return hyphen?.[1] ?? null
}

function displayName(title: string, producer: string | null): string {
  if (!producer) return title
  for (const separator of [' – ', ' -']) {
    if (title.endsWith(`${separator}${producer}`)) return title.slice(0, -(separator.length + producer.length))
  }
  return title
}

export function ProductCard({ product, language, categoryPath }: { product: PublicContentRecord; language: ContentLocale; categoryPath: string }) {
  const { t } = useTranslation('common')
  const record = product.languages[language] ?? product.languages.it ?? product.languages.en
  if (!record) return null
  const fallback = !product.languages[language]
  const producer = sourceProducer(product)
  const imagePath = product.id === 'prd-emotrace-tracciabilita-sangue' && record.media[1] ? record.media[1] : record.primaryMedia
  const media = sourceMedia.find(item => item.path === imagePath)
  const name = displayName(record.title, producer)
  return <article className="catalog-product-card">
    <LocalizedLink
      to={product.id}
      className="catalog-product-link"
      state={{ catalogContext: { categoryId: product.categoryId, categoryPath } }}
    >
      <div className="catalog-product-image">
        {media
          ? <img src={media.path} width={media.width} height={media.height} alt={t('catalog.productImageAlt', { product: name })} loading="lazy" decoding="async" />
          : <span className="catalog-media-empty"><ImageOff aria-hidden="true" size={28} />{t('catalog.mediaUnavailable')}</span>}
      </div>
      <div className="catalog-product-copy">
        <p className="catalog-card-kind">{t('catalog.productKind')}</p>
        <h2>{name}</h2>
        <p className="catalog-producer">{producer ? t('catalog.producer', { producer }) : t('catalog.producerUnavailable')}</p>
        {fallback && <p className="catalog-source-note">{t('catalog.languageFallback')}</p>}
        <span className="catalog-card-action">{t('catalog.viewProduct')}<ArrowUpRight aria-hidden="true" size={21} /></span>
      </div>
    </LocalizedLink>
  </article>
}
